import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Bank,
  CalendarDots,
  CircleNotch,
  Clock,
  Users,
  VideoCamera,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiGet, apiSend } from "@/lib/api-error";
import { formatCurrency, formatDateTimeBs } from "@/lib/format";
import { toast } from "@/lib/toast";

type PaymentStatus =
  | "pending"
  | "pending_transfer"
  | "paid"
  | "refunded"
  | "failed";

interface BankDetails {
  bankName: string;
  accountNumber: string;
  recipientName: string;
  reference: string;
  amount: number;
  currency: string;
}

interface MyEnrollment {
  id: string;
  cycleName: string;
  therapistUserName: string;
  paymentStatus: PaymentStatus;
  priceCharged: number;
  firstSessionAt: string | null;
  sessionCount: number;
  cancelledAt: string | null;
  cycleCancelledAt: string | null;
  // Next session not yet over; its Meet link only on a paid seat.
  nextSessionAt: string | null;
  nextSessionMeetLink: string | null;
  bankDetails: BankDetails | null;
}

const QUERY_KEY = ["client", "group-therapies", "mine"] as const;

const STATUS_TONE: Record<string, string> = {
  paid: "border-emerald-500/25 bg-emerald-500/10 text-emerald-700",
  pending: "border-amber-500/25 bg-amber-500/10 text-amber-700",
  pending_transfer: "border-amber-500/25 bg-amber-500/10 text-amber-700",
  refunded: "border-sky-500/25 bg-sky-500/10 text-sky-700",
  failed: "border-rose-500/25 bg-rose-500/10 text-rose-700",
  cancelled: "border-border bg-muted text-muted-foreground",
};

export function MyGroupTherapies() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState<MyEnrollment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiGet<{ enrollments: MyEnrollment[] }>(
        "/api/group-therapies/enrollments/mine",
        t("clientGroupTherapies.loadError")
      ).then((r) => r.enrollments),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(
        `/api/group-therapies/enrollments/${id}/cancel`,
        "POST",
        undefined,
        t("clientGroupTherapies.cancelError")
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setCancelling(null);
      toast.success(t("clientGroupTherapies.cancelSuccess"));
    },
    onError: (err: Error) => {
      toast.error(err.message || t("clientGroupTherapies.cancelError"));
    },
  });

  const enrollments = data ?? [];

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <CircleNotch className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card px-6 py-14 text-center">
        <Users className="size-9 text-primary/40" weight="duotone" />
        <p className="mt-3 text-sm text-muted-foreground">
          {t("clientGroupTherapies.empty")}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: "/grupne-terapije" })}
        >
          {t("clientGroupTherapies.browse")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {enrollments.map((e) => {
        const status = e.cancelledAt ? "cancelled" : e.paymentStatus;
        const startsInFuture =
          !!e.firstSessionAt && new Date(e.firstSessionAt).getTime() > Date.now();
        const canCancel =
          !e.cancelledAt &&
          e.paymentStatus !== "refunded" &&
          e.paymentStatus !== "failed" &&
          startsInFuture;
        const showNextSession =
          !e.cancelledAt && e.paymentStatus === "paid" && !!e.nextSessionAt;

        return (
          <article
            key={e.id}
            className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm sm:p-5"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3
                    className="truncate text-base font-semibold text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {e.cycleName}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                      STATUS_TONE[status] ?? STATUS_TONE.cancelled
                    }`}
                  >
                    {t(`clientGroupTherapies.status.${status}`)}
                  </span>
                </div>
                <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                  <Meta
                    icon={<Users className="size-4" />}
                    label={t("clientGroupTherapies.therapist")}
                    value={e.therapistUserName}
                  />
                  {showNextSession ? (
                    <Meta
                      icon={<CalendarDots className="size-4" />}
                      label={t("clientGroupTherapies.nextSession")}
                      value={formatDateTimeBs(e.nextSessionAt!)}
                    />
                  ) : (
                    <Meta
                      icon={<CalendarDots className="size-4" />}
                      label={t("clientGroupTherapies.firstSession")}
                      value={e.firstSessionAt ? formatDateTimeBs(e.firstSessionAt) : "-"}
                    />
                  )}
                  <Meta
                    icon={<Clock className="size-4" />}
                    label={t("clientGroupTherapies.sessions")}
                    value={String(e.sessionCount)}
                  />
                </dl>
                {showNextSession &&
                  (e.nextSessionMeetLink ? (
                    <a
                      href={e.nextSessionMeetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      <VideoCamera className="size-3.5" />
                      {t("client.sessions.joinMeet")}
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t("clientGroupTherapies.linkSoon")}
                    </p>
                  ))}
              </div>

              <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                <p
                  className="text-lg font-semibold tabular-nums text-foreground"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {formatCurrency(e.priceCharged)}
                </p>
                {canCancel && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCancelling(e)}
                    disabled={cancelMutation.isPending}
                  >
                    {t("clientGroupTherapies.cancel")}
                  </Button>
                )}
              </div>
            </div>

            {e.bankDetails && !e.cancelledAt && !e.cycleCancelledAt && (
              <div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-4">
                <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Bank className="size-4" /> {t("clientGroupTherapies.bankTitle")}
                </h4>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t("clientGroupTherapies.bankHint")}
                </p>
                <dl className="space-y-1 text-sm">
                  <BankRow label={t("clientGroupTherapies.bankName")} value={e.bankDetails.bankName} />
                  <BankRow label={t("clientGroupTherapies.account")} value={e.bankDetails.accountNumber} />
                  <BankRow label={t("clientGroupTherapies.recipient")} value={e.bankDetails.recipientName} />
                  <BankRow label={t("clientGroupTherapies.reference")} value={e.bankDetails.reference} />
                  <BankRow
                    label={t("clientGroupTherapies.amount")}
                    value={formatCurrency(e.bankDetails.amount)}
                  />
                </dl>
              </div>
            )}
          </article>
        );
      })}

      <ConfirmDialog
        open={cancelling !== null}
        tone="destructive"
        title={t("clientGroupTherapies.cancelTitle")}
        description={t("clientGroupTherapies.cancelBody")}
        confirmLabel={t("clientGroupTherapies.cancelConfirm")}
        cancelLabel={t("clientGroupTherapies.cancelBack")}
        isPending={cancelMutation.isPending}
        onClose={() => setCancelling(null)}
        onConfirm={() => cancelling && cancelMutation.mutate(cancelling.id)}
      />
    </div>
  );
}

function Meta({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span>{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function BankRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  );
}
